import { getActiveBrandConfig, getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../config/environment.config";

/**
 * Présentation du changelog d'une version (table api_version, champ JSON `changelog`).
 *
 * Deux écrans lisent le même JSON :
 * - la fenêtre « Nouvelle version » à la première ouverture, qui ne montre que les
 *   entrées marquées `highlight` (trois au plus) et renvoie vers la page complète ;
 * - la page « Quoi de neuf » du tableau de bord (maquette 13a du 2 novembre 2026), qui
 *   met en avant la nouveauté principale (`featured`, à défaut la première marquée
 *   `highlight`) avec un aperçu et un bouton pour l'essayer (`path`), puis regroupe
 *   toutes les entrées par `category`, chacune disant où la trouver (`where`).
 * Tous ces champs sont facultatifs : sans `highlight`, la fenêtre prend les premières
 * entrées ; sans `category`, l'entrée va dans la rubrique « Autres ».
 */

/** Version du build (package.json via REACT_APP_VERSION), comparée à celle enregistrée. */
export const APP_VERSION = process.env.REACT_APP_VERSION || "3.4.0";
/** Clé localStorage de la dernière version vue par l'utilisateur. */
export const VERSION_KEY = "teachr_app_version";
/** Version dont la page Quoi de neuf doit s'ouvrir après la reconnexion demandée par la fenêtre. */
export const WHATS_NEW_PENDING_KEY = "teachr_whats_new_pending";

export const WHATS_NEW_PATH = () => `/${getActiveBrandConfig()}/tableau-de-bord/nouveautes`;

/** Nombre maximal de points forts dans la fenêtre de version. */
export const HIGHLIGHT_MAX = 3;

export type ChangelogCategory = "declaration" | "clients" | "documents" | "autres";

export interface ChangelogEntry {
    title: string;
    description: string;
    /** Mis en avant dans la fenêtre de version. */
    highlight?: boolean;
    /** Rubrique de la page Quoi de neuf. */
    category?: string;
    /** La nouveauté principale de la version, en tête de la page Quoi de neuf (une seule). */
    featured?: boolean;
    /** Où la trouver : « Déclarer un cours, étape Planning ». */
    where?: string;
    /** Chemin dans le tableau de bord pour l'essayer, relatif à `/{marque}/tableau-de-bord` : « /cours/declarer ». */
    path?: string;
    /** Aperçu illustré de la nouveauté principale : « calendar » (dates cochées) ou rien. */
    preview?: "calendar";
    /** Légende de l'aperçu : « 2 dates cochées, jeudis 15 et 22, 16h00 à 17h30 ». */
    previewCaption?: string;
}

export interface VersionInfo {
    version: string;
    hasEnded: boolean;
    isMaintenance: boolean;
    changelog: ChangelogEntry[] | null;
}

export interface ChangelogGroup {
    key: ChangelogCategory;
    label: string;
    entries: ChangelogEntry[];
}

/**
 * Rubriques dans l'ordre d'affichage, nommées par ce que fait le prof plutôt que par
 * des catégories techniques (maquette 13a). La dernière recueille les catégories inconnues.
 */
export const CHANGELOG_CATEGORIES: Array<{ key: ChangelogCategory; label: string }> = [
    { key: "declaration", label: `Déclarer ${getActivePrestationBrandConfig("", "un ", "une ")}` },
    { key: "clients", label: `Vos ${getActiveClientBrandConfig("s")}` },
    { key: "documents", label: "Documents et accueil" },
    { key: "autres", label: "Autres" },
];

const KNOWN_CATEGORIES = new Set<string>(CHANGELOG_CATEGORIES.map((c) => c.key));

/** Rubrique d'une entrée : sa catégorie si elle est connue, sinon « autres ». */
export const changelogCategory = (entry: ChangelogEntry): ChangelogCategory => {
    const raw = (entry.category ?? "").trim().toLowerCase();
    return KNOWN_CATEGORIES.has(raw) ? (raw as ChangelogCategory) : "autres";
};

/**
 * Entrées de la fenêtre de version : celles marquées `highlight`, dans l'ordre du JSON,
 * plafonnées à `max`. Sans aucune entrée marquée, les premières du JSON, pour qu'un
 * changelog ancien (sans le champ) continue de s'afficher.
 */
export const highlightedEntries = (entries: ChangelogEntry[] | null | undefined, max = HIGHLIGHT_MAX): ChangelogEntry[] => {
    const list = entries ?? [];
    const flagged = list.filter((e) => e.highlight === true);
    return (flagged.length > 0 ? flagged : list).slice(0, max);
};

/** Groupes non vides, dans l'ordre des rubriques, chaque entrée gardant l'ordre du JSON. */
export const groupChangelog = (entries: ChangelogEntry[] | null | undefined): ChangelogGroup[] => {
    const list = entries ?? [];
    return CHANGELOG_CATEGORIES
        .map(({ key, label }) => ({ key, label, entries: list.filter((e) => changelogCategory(e) === key) }))
        .filter((group) => group.entries.length > 0);
};

/** Libellé du lien vers la page complète : « Voir les 13 nouveautés », ou au singulier. */
export const seeAllLabel = (count: number): string =>
    count > 1 ? `Voir les ${count} nouveautés` : "Voir la nouveauté";

/** « 14 nouveautés », « 1 nouveauté ». */
export const countLabel = (count: number): string => (count > 1 ? `${count} nouveautés` : `${count} nouveauté`);

/**
 * Nouveauté principale de la version (bloc en tête de la page Quoi de neuf) : la
 * première marquée `featured`, à défaut la première marquée `highlight`, à défaut la
 * première du changelog. Null quand le changelog est vide.
 */
export const featuredEntry = (entries: ChangelogEntry[] | null | undefined): ChangelogEntry | null => {
    const list = entries ?? [];
    return list.find((e) => e.featured === true) ?? list.find((e) => e.highlight === true) ?? list[0] ?? null;
};

/** Chemin complet dans le tableau de bord d'un `path` du changelog (« /cours/declarer » → « /teachr/tableau-de-bord/cours/declarer »). */
export const changelogEntryPath = (entry: ChangelogEntry): string | null =>
    entry.path ? `/${getActiveBrandConfig()}/tableau-de-bord${entry.path.startsWith("/") ? "" : "/"}${entry.path}` : null;

/** Version au format de l'API (3.4.0 → 3_4_0). */
export const apiVersionKey = (version: string): string => version.replace(/\./g, "_");
